import ppUtilities from './ppUtilities.js';
import axios from 'axios';
import { mountShoppingCartBanner } from '@/features/cart/components/mountShoppingCartBanner.js';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import { hasInvalidItemPrice, recalculateCartPrices, toCartModel } from '../../utils/ecommerce/cart.js';
import { getCartCache } from '../../utils/ecommerce/http-cart.js';
import { Analytics } from '../../services/analytics/analytics-service.js';
import { getRootDomain } from '../../utils/common/url.js';
import {EVENTS} from '@/services/analytics/constants';
import * as _ from 'lodash-es';

const _storeVariables = window.storeVariables || {};

class ECart {
	constructor(sessionData, onItemsAddedToCart = () => {}) {
		ppUtilities.jsCookie(); // init js-cookie
		this.analytics = Analytics.getInstance();
		this.sessionData = sessionData;
		this.selectedProducts = [];
		this.sessionMetadataUpdate = null;
		this.storeVariables = _storeVariables;
		this.istApiUrl = this.storeVariables.eCommUrl;
		this.state = {};
		this.itemsAdded = 0;
		this.parentApp, this.parentAppCallback;
		this.onItemsAddedToCart = onItemsAddedToCart;
		this.productAdded = false;

		this.onApplicationMounted();
		this.currDomain = this.getCookieDomain();
	}
	setState(update) {
		let keyToUpdate = _.keys(update)[0];
		this.state = _.set(this.state, keyToUpdate, update[keyToUpdate]);
		this.render();
	}

	getCookieDomain() {
		let cookieDom = '';
		try {
			cookieDom = getRootDomain(location.host);
		} catch (e) {
			console.error(`[Cookie domain issue: ${e}]`);
		}

		return cookieDom; // Cookie if issue do not set let browser to set default
	}

	onApplicationMounted() {
		let cartId = this.getCartId();

		if (cartId != undefined) {
			this.getCartState(cartId);
		} else {
			if (this.isUserLoggedIn()) {
				cartId = this.getCartIdFromUser();
				if (cartId) {
					this.getCartState(cartId);
				}
			}
		}
	}

	isUserLoggedIn() {
		return Cookies.get('esri_auth') ? true : false;
	}

	getAuthCookie() {
		return Cookies.get('esri_auth');
	}

	getAuthToken() {
		const esriAuthCookie = this.getAuthCookie();
		let token = null;
		if (esriAuthCookie) {
			let parsedEsriAuthCookie = JSON.parse(decodeURIComponent(esriAuthCookie));
			token = parsedEsriAuthCookie.token;
		}
		return token;
	}

	getCartIdFromUser() {
		let token = this.getAuthToken();
		let apiURl = `${this.istApiUrl}/carts?latest=1`;

		axios({
			url: apiURl,
			method: 'get',
			headers: {
				Authorization: token ? token : 'anonymous',
			},
			withCredentials: true,
			responseType: 'json',
		})
			.then((response) => {
				let cart = response.data[0];
				this.sessionMetadataUpdate = cart.metadata;
				this.setCookieCart(cart._id);
				this.setState({ cart });
			})
			.catch((error) => {
				console.error('Create cart error: ', error);
				this.removeExistingCartCookie(error);
			});
	}

	addToCart(products, app, callback) {
		this.selectedProducts = products;
		this.parentApp = app;
		this.parentAppCallback = app[callback];

		// Set add to cart flag and count of items added
		this.productAdded = true;
		this.itemsAdded = this.selectedProducts.reduce((acc, p) => acc + p.qty, 0);

		// Create or update cart and emit analytics events on success
		const cartId = this.getCartId();
		if (cartId == null) {
			this.createCart().then(() => {
				const [event, legacyEvents] = this.createAddProductAnalyticsEvent(this.selectedProducts);
				this.analytics.addEvent(EVENTS.CART_ADD_OPEN, event);
				if (window._satellite) {
					legacyEvents.forEach((prodTrack) => window._satellite.track('scAddOpen', prodTrack));
				}
			});
		} else {
			this.addProductToApi(cartId).then(() => {
				const [event, legacyEvents] = this.createAddProductAnalyticsEvent(this.selectedProducts);
				this.analytics.addEvent(EVENTS.CART_ADD, event);
				if (window._satellite) {
					legacyEvents.forEach((prodTrack) => window._satellite.track('scAdd', prodTrack));
				}
				this.addSkusToCookie(this.selectedProducts.map((product) => product.activeSku));
			});
		}
	}

	createAddProductAnalyticsEvent(selectedProducts) {
		const eventData = [];
		const legacyEventData = [];

		selectedProducts.forEach((product) => {
			const productMaster = product.products?.find((p) => product?.activeSku === p?.sku) || {};
			const activePrice = product.activePrice ? product.activePrice.toString() : '0';
			const productInfo = {
				sku: product.activeSku,
				price: activePrice,
				qty: product.qty,
				name: productMaster?.productTitle || product.title,
				currency: productMaster?.currency,
			};
			const legacyProductInfo = {
				sku: product.activeSku,
				price: activePrice,
				qty: product.qty,
				product: product.title,
			};

			eventData.push({ productInfo });
			legacyEventData.push(legacyProductInfo);
		});

		return [{ cartID: this.getCartId(), items: eventData }, legacyEventData];
	}

	addSkusToCookie(skus) {
		const cookieName = 'cartSKUList';
		const cookieExpiry = {
			expires: 7,
			path: '/',
		};
		const skusInCookie = Cookies.get(cookieName);
		const skuList = skusInCookie ? JSON.parse(skusInCookie) : [];
		skus.forEach((sku) => {
			if (!skuList.includes(sku)) {
				skuList.push(sku);
			}
		});
		Cookies.set(cookieName, JSON.stringify(skuList), cookieExpiry);
	}

	getCartId() {
		return Cookies.get('eCommCartId');
	}

	setCookieCart(cartId) {
		let cartObj = {
			expires: 2000,
			path: '/',
		};
		if (this.currDomain !== '') {
			cartObj.domain = this.currDomain; // if issue setting cookie domain let browser default to current domain.
		}
		Cookies.set('eCommCartId', cartId, cartObj);
	}

	createCart() {
		let token = this.getAuthToken();
		let requestModel = this.getStringifiedCartModel(this.state?.cart?.lineItems, this.selectedProducts);
		let apiURl = `${this.istApiUrl}/carts`;
		return axios({
			url: apiURl,
			method: 'post',
			headers: {
				Authorization: token ? token : 'anonymous',
			},
			data: requestModel,
			withCredentials: true,
			responseType: 'json',
		})
			.then((response) => {
				let cart = response.data;
				this.sessionMetadataUpdate = cart.metadata;
				this.setCookieCart(cart._id);
				this.setState({ cart });
			})
			.catch((error) => {
				console.error('create cart error: ', error);
			});
	}

	findActiveProduct(selectedProduct) {
		if (!selectedProduct?.products?.length) return null;

		const selProd = selectedProduct.products.find((prod) => selectedProduct.activeSku === prod.sku);

		return selProd;
	}

	findLineItem(lineItems, selectedProduct) {
		if (!lineItems?.length || !selectedProduct) return null;

		const selProd = this.findActiveProduct(selectedProduct);
		return lineItems.find((prod) => prod.materialNumber === parseInt(selProd.sku));
	}

	createLineItem(selectedProduct) {
		if (!selectedProduct) return null;

		const selProd = this.findActiveProduct(selectedProduct);
		const startDate = dayjs();
		const endDate = dayjs(startDate).add(1, 'years').subtract(1, 'days');

		const newLineItem = {
			termLicense: true,
			quantity: selectedProduct.qty,
			materialNumber: parseInt(selProd.sku),
			price: selProd.amount,
			productName: selProd.productTitle,
			startDate: dayjs(startDate).format('YYYY-MM-DD') + 'T00:00:00Z',
			endDate: dayjs(endDate).format('YYYY-MM-DD') + 'T00:00:00Z',
			metadata: {
				productInfo: {
					productPath: selectedProduct.productPath,
					productIcon: selectedProduct.icon,
					shortDescription: selectedProduct.bullets,
					pricingPageUrl: window.location.pathname,
				},
				pricing: {
					pricingTiers: [
						{
							_id: 'mockPricingTier',
							amount: selProd.__amount__,
							vatAmount: selProd.__vatAmount__,
							qtyFrom: null,
							qtyTo: null,
						},
					],
				},
			},
		};

		return newLineItem;
	}

	createLineItems(existingLineItems, selectedProducts) {
		const lineItems = existingLineItems?.length ? [...existingLineItems] : [];
		selectedProducts.forEach((selectedProduct) => {
			const newLineItem = this.createLineItem(selectedProduct);
			const existingLineItem = this.findLineItem(lineItems, selectedProduct);

			if (newLineItem && existingLineItem) {
				existingLineItem.quantity += newLineItem.quantity;
			} else if (newLineItem && !existingLineItem) {
				lineItems.push(newLineItem);
			}
		});

		return lineItems;
	}

	createCartModel(lineItems) {
		const initialCartData = this.state?.cart ? this.state.cart : this.sessionData;
		let model = toCartModel({
			...initialCartData,
			sessionMetadataUpdate: this.sessionMetadataUpdate,
			lineItems: lineItems,
		});
		return model;
	}

	getStringifiedCartModel(lineItems, selectedProducts) {
		const model = this.createCartModel(this.createLineItems(lineItems, selectedProducts));
		return JSON.stringify(model);
	}

	removeExistingCartCookie(error) {
		let cartCookieId = this.getCartId();
		if (cartCookieId) {
			Cookies.remove('eCommCartId', { domain: this.currDomain });
			console.error('Removing cookie cart - no response from api: ', error);
		}
	}

	updateCartPrices(cart) {
		recalculateCartPrices(cart);
		const requestModel = this.getStringifiedCartModel(cart.lineItems);
		this.updateCart(this.getCartId(), requestModel);
	}

	addProductToApi(cartId) {
		const requestModel = this.getStringifiedCartModel(this.state?.cart?.lineItems, this.selectedProducts);
		return this.updateCart(cartId, requestModel);
	}

	updateCart(cartId, requestModel) {
		let token = this.getAuthToken();
		let apiURl = `${this.istApiUrl}/carts/${cartId}`;

		return axios({
			url: apiURl,
			method: 'put',
			headers: {
				Authorization: token ? token : 'anonymous',
			},
			data: requestModel,
			withCredentials: true,
			responseType: 'json',
		})
			.then((response) => {
				let cart = response.data.cart;
				this.sessionMetadataUpdate = cart.metadata;
				this.setState({ cart });
			})
			.catch((error) => {
				console.error('addProductToApi: ', error);
			});
	}

	getCartState(cartId) {
		// getCartState gets call if there is an existing cart for user
		let token = this.getAuthToken();
		getCartCache(cartId, token)
			.getData()
			.then((response) => {
				let cart = response.data[0];
				if (hasInvalidItemPrice(cart)) this.updateCartPrices(cart);
				this.sessionMetadataUpdate = cart.metadata;
				this.setState({ cart });
			})
			.catch((error) => {
				console.error('getCartState error: ', error);
				this.removeExistingCartCookie(error);
			});
	}

	render() {
		// do not render if cart is empty
		if (!this.state.cart || !this.state.cart.lineItems.length) {
			return null;
		}
		if (this.itemsAdded) {
			window.esriShoppingCart.addItemsToCart(this.itemsAdded);
			const items = this.selectedProducts.map((product) => ({
				productName: product.products.find((item) => item.sku === product.activeSku)?.productTitle || product.title,
			}));
			const totalQuantity = this.state.cart.lineItems.reduce((total, item) => total + item.quantity, 0);
			this.onItemsAddedToCart({items, totalQuantity});
		}
		mountShoppingCartBanner({
			lineItems: this.state.cart.lineItems,
			itemsAdded: this.itemsAdded,
			cartUrl: window.storeVariables?.cartUrl,
		});
		this.parentAppCallback?.();
		this.productAdded = false;
		this.itemsAdded = 0;
	}
}

export default ECart;
